import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "../ui/button";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_biz-hub-206/artifacts/n4vp97w9_logo%20size.png";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-brand-sand/95 backdrop-blur-md border-b border-brand-line shadow-[0_2px_20px_rgba(0,0,0,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-10 lg:px-14 py-4">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-3 group">
          <span className="grid place-items-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white shadow-md ring-1 ring-brand-line overflow-hidden group-hover:scale-105 transition-transform">
            <img
              src={LOGO_URL}
              alt="Rafael Costa Garden Care logo"
              className="w-full h-full object-contain p-1"
            />
          </span>
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-bold text-brand-moss-deep text-base sm:text-lg">Rafael Costa Garden Care</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-brand-charcoal/70 -mt-0.5">Friendly gardeners</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-medium text-brand-moss-deep/80 hover:text-brand-moss transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 hover:after:w-full after:h-[2px] after:bg-brand-terracotta after:transition-all"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:07467946894"
            data-testid="header-phone-link"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-brand-moss-deep hover:text-brand-moss"
          >
            <Phone className="w-4 h-4" /> 07467 946894
          </a>
          <Button
            asChild
            data-testid="header-cta-button"
            className="hidden sm:inline-flex rounded-full bg-brand-moss hover:bg-brand-moss-dark text-white px-5 h-10 font-semibold shadow-md hover:-translate-y-0.5 transition-all"
          >
            <a href="#contact">Free Quote</a>
          </Button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-brand-line bg-white"
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-brand-sand">
          <nav className="px-6 py-5 flex flex-col gap-2">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="py-2 text-base font-medium text-brand-moss-deep border-b border-brand-line/60 last:border-0"
              >
                {n.label}
              </a>
            ))}
            <a
              href="tel:07467946894"
              data-testid="mobile-phone-link"
              className="mt-2 inline-flex items-center gap-2 text-brand-moss font-semibold"
            >
              <Phone className="w-4 h-4" /> 07467 946894
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
